import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import OnboardingLogo from "../../components/onboarding/OnboardingLogo";
import {
  onboardingShell, card, label, inputStyle, primaryBtn, errorText,
} from "../../components/onboarding/styles";
import { API_BASE } from "../../lib/api";
import { getCurrentCustomer, setCurrentCustomer } from "../../lib/customerSession";

/**
 * Mandatory phone-collection step shown right after a Google-authenticated
 * user's first sign-in. Blocks the rest of onboarding (Business Profile /
 * trial) until a phone number is on file — see /app/auth_testing.md.
 */
export default function CollectPhonePage() {
  const navigate = useNavigate();
  const [customer] = useState(() => {
    try { return getCurrentCustomer(); } catch (_e) { return null; }
  });
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!customer?.id) navigate("/login", { replace: true });
  }, [customer, navigate]);

  const onPhoneChange = (e) => setPhone((e.target.value || "").replace(/\D/g, "").slice(0, 10));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/complete-phone`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ customer_id: customer.id, phone: `+91${phone}` }),
      });
      if (res.ok) {
        const data = await res.json();
        setCurrentCustomer(data.customer);
        if (data.profile_required) {
          sessionStorage.setItem("onboarding_email", data.customer.email);
          sessionStorage.setItem("onboarding_customer_id", data.customer.id);
          navigate("/business-profile", { replace: true });
        } else {
          navigate("/executive", { replace: true });
        }
        return;
      }
      let detail = "";
      try { const j = await res.json(); detail = j?.detail || ""; } catch (_e) {}
      if (detail === "phone_already_registered") {
        setError("This number is already registered to another account.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (_e) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="collect-phone-page" style={onboardingShell}>
      <div style={{ marginBottom: 24 }}><OnboardingLogo /></div>
      <div style={{ ...card, width: 420, maxWidth: "100%", padding: 40 }}>
        <h2 style={{ color: "#1E3A5F", fontSize: 22, fontWeight: 700, marginBottom: 4 }}>One last step</h2>
        <p style={{ color: "#6B7280", fontSize: 14, marginBottom: 20 }}>
          Add your mobile number so our team can reach you about your C-ORE setup.
        </p>
        <hr style={{ borderColor: "#F3F4F6", borderTop: "1px solid #F3F4F6", borderBottom: "none", borderLeft: "none", borderRight: "none", marginBottom: 24 }} />

        <form onSubmit={onSubmit} noValidate>
          <div style={{ marginBottom: 20 }}>
            <label style={label} htmlFor="collect-phone-input">Mobile Number</label>
            <div style={{ display: "flex" }}>
              <span style={{
                background: "#F3F4F6", border: "1px solid #D1D5DB", borderRight: "none",
                borderRadius: "8px 0 0 8px", padding: "0 12px", height: 44,
                display: "flex", alignItems: "center", fontSize: 13, color: "#6B7280",
              }}>+91</span>
              <input
                data-testid="collect-phone-input"
                id="collect-phone-input"
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={onPhoneChange}
                placeholder="9876543210"
                autoComplete="tel-national"
                style={{ ...inputStyle(false), borderRadius: "0 8px 8px 0", flex: 1, appearance: "textfield" }}
              />
            </div>
            {error && <p data-testid="collect-phone-error" style={errorText}>{error}</p>}
          </div>

          <button
            data-testid="collect-phone-submit"
            type="submit"
            disabled={loading}
            style={{
              ...primaryBtn,
              background: loading ? "#10b981" : "#059669",
              opacity: loading ? 0.85 : 1,
              cursor: loading ? "default" : "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
          >
            {loading ? "Saving…" : "Continue"}
          </button>
        </form>
      </div>
    </div>
  );
}
